import { useEffect } from "react";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";

const MARKER_ID = "gtm-injected-code";

export function GTMLoader() {
  const { settings } = useSiteSettings();

  useEffect(() => {
    // Clean up any previously injected GTM nodes
    document.querySelectorAll(`[data-gtm-injected="true"]`).forEach((n) => n.remove());
    const marker = document.getElementById(MARKER_ID);
    if (marker) marker.remove();

    const code = (settings.gtm_code || "").trim();
    if (!code) return;

    // Parse the pasted snippet and re-create nodes so <script> tags execute.
    const template = document.createElement("template");
    template.innerHTML = code;

    // Preserve original order while inserting as the FIRST children of <head>
    // (i.e. immediately after the opening <head> tag, per Google's instructions).
    const nodes = Array.from(template.content.childNodes);
    const injected: Node[] = [];
    const headInserts: Element[] = [];

    nodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === "SCRIPT") {
        const src = node as HTMLScriptElement;
        const script = document.createElement("script");
        for (const attr of Array.from(src.attributes)) {
          script.setAttribute(attr.name, attr.value);
        }
        script.text = src.textContent || "";
        script.setAttribute("data-gtm-injected", "true");
        headInserts.push(script);
        injected.push(script);
      } else if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === "NOSCRIPT") {
        // <noscript> belongs in <body> per HTML rules
        const ns = document.createElement("noscript");
        ns.innerHTML = (node as HTMLElement).innerHTML;
        ns.setAttribute("data-gtm-injected", "true");
        document.body.prepend(ns);
        injected.push(ns);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const clone = (node as Element).cloneNode(true) as Element;
        clone.setAttribute("data-gtm-injected", "true");
        headInserts.push(clone);
        injected.push(clone);
      }
    });

    // Prepend in reverse so the final DOM order matches the pasted snippet
    // and everything ends up before any other <head> children.
    for (let i = headInserts.length - 1; i >= 0; i--) {
      document.head.prepend(headInserts[i]);
    }

    const m = document.createElement("meta");
    m.id = MARKER_ID;
    m.setAttribute("name", "gtm-loaded");
    m.setAttribute("content", "true");
    document.head.appendChild(m);

    return () => {
      injected.forEach((n) => n.parentNode?.removeChild(n));
      const mm = document.getElementById(MARKER_ID);
      if (mm) mm.remove();
    };
  }, [settings.gtm_code]);

  return null;
}
